import React from 'react';
import { Layout } from '@/components/layout/Layout';
import Seo from '@/components/Seo';
import UseCasesNav from '@/components/use-cases/UseCasesNav';
import { Button } from '@/components/ui/button';

export default function UseCaseTemplate({
  seoTitle,
  seoDescription,
  path,
  title,
  subtitle,
  summary,
  challenges,
  pillars,
  timeline,
  deliverables,
  kpis,
  example,
}) {
  return (
    <Layout>
      <Seo title={seoTitle} description={seoDescription} path={path} />

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 space-y-8">
          <div className="flex flex-col gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-500 font-body">Use Cases</p>
              <h1 className="text-4xl lg:text-5xl font-black font-heading text-slate-900 mt-4">
                {title}
              </h1>
              <p className="text-lg text-slate-600 mt-4 max-w-3xl font-body">
                {subtitle}
              </p>
            </div>
            <UseCasesNav />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {summary.map((item) => (
              <div key={item} className="p-5 rounded-xl border border-slate-200 bg-white">
                <p className="text-slate-700 font-body">{item}</p>
              </div>
            ))}
          </div>

          <section className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <h2 className="text-sm font-black uppercase tracking-wide text-slate-500 mb-4">
              Key Challenges
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {challenges.map((item) => (
                <div key={item.title} className="space-y-2">
                  <h3 className="text-base font-bold text-slate-900">{item.title}</h3>
                  <p className="text-sm text-slate-600 font-body">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-sm font-black uppercase tracking-wide text-slate-500 mb-4">
              Programme Pillars
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {pillars.map((item) => (
                <div key={item.title} className="p-5 rounded-xl border border-slate-200 bg-white">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600 font-body">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="border border-slate-200 rounded-2xl p-6 bg-white">
            <h2 className="text-sm font-black uppercase tracking-wide text-slate-500 mb-4">
              Delivery Timeline
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {timeline.map((step, index) => (
                <div key={step.title} className="space-y-2">
                  <div className="text-xs font-black text-slate-500">Step {index + 1}</div>
                  <h3 className="text-base font-bold text-slate-900">{step.title}</h3>
                  <p className="text-sm text-slate-600 font-body">{step.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="p-5 rounded-xl border border-slate-200 bg-white">
              <h3 className="text-sm font-black uppercase tracking-wide text-slate-500 mb-3">Deliverables</h3>
              <ul className="space-y-2 text-slate-700 font-body text-sm">
                {deliverables.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="p-5 rounded-xl border border-slate-200 bg-white">
              <h3 className="text-sm font-black uppercase tracking-wide text-slate-500 mb-3">KPIs We Track</h3>
              <ul className="space-y-2 text-slate-700 font-body text-sm">
                {kpis.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="p-5 rounded-xl border border-slate-200 bg-white">
              <h3 className="text-sm font-black uppercase tracking-wide text-slate-500 mb-3">Proof</h3>
              <p className="text-slate-900 font-semibold mb-2">{example.title}</p>
              <p className="text-slate-600 font-body text-sm">{example.result}</p>
            </div>
          </section>

          <div className="flex justify-start">
            <Button asChild className="bg-slate-900 text-white hover:bg-slate-800">
              <a href="/contact">Talk to Us</a>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
